/**
 * Feature Flags for Design System Migration
 *
 * Controls rollout of new UI/UX features. Each flag enables a specific
 * phase of the migration while preserving the old implementation.
 */

export const FeatureFlags = {
  // Phase 2: Navigation System
  NEW_NAVIGATION: true,       // Sidebar (desktop) + bottom tabs (mobile) - ENABLED FOR PHASE 3

  // Phase 3: Dashboard Redesign
  NEW_DASHBOARD: true,         // Single KPI + next action card - ENABLED

  // Phase 4: Pipeline Views
  PIPELINE_BOARD: false,       // Kanban board view

  // Phase 2: Command Palette
  COMMAND_PALETTE: true,       // ⌘K quick actions - ENABLED

  // Phase 5: Audit Wizard
  NEW_AUDIT_WIZARD: false,     // 3-step wizard (replaces long form) - DISABLED - using original single-page form

  // Phase 5: Results Drawer
  RESULTS_DRAWER: true,        // Sheet/drawer (replaces full page) - ENABLED

  // Result Page Style
  MINIMAL_RESULT_SCREEN: true, // Use minimal result page - ENABLED
} as const;

export type FeatureFlagKey = keyof typeof FeatureFlags;

/**
 * Check if a feature flag is enabled
 *
 * Checks localStorage override first (for testing), then falls back
 * to the static FeatureFlags configuration.
 */
export function isFeatureEnabled(flag: FeatureFlagKey): boolean {
  // Check localStorage override for testing
  const override = localStorage.getItem(`feature.${flag}`);
  if (override !== null) {
    return override === 'true';
  }

  // Fall back to static config
  return FeatureFlags[flag];
}

/**
 * Enable a feature flag (for testing/admin only)
 *
 * Sets a localStorage override. Useful for:
 * - Testing new features before rollout
 * - Admin/beta user preview
 * - Local development
 */
export function enableFeature(flag: FeatureFlagKey): void {
  localStorage.setItem(`feature.${flag}`, 'true');
  console.log(`[FeatureFlags] Enabled: ${flag}`);
}

/**
 * Disable a feature flag (for testing/admin only)
 */
export function disableFeature(flag: FeatureFlagKey): void {
  localStorage.setItem(`feature.${flag}`, 'false');
  console.log(`[FeatureFlags] Disabled: ${flag}`);
}

/**
 * Clear all feature flag overrides
 */
export function clearFeatureOverrides(): void {
  Object.keys(FeatureFlags).forEach(key => {
    localStorage.removeItem(`feature.${key}`);
  });
  console.log('[FeatureFlags] Cleared all overrides');
}

/**
 * Get all feature flag states (for debugging)
 */
export function getFeatureFlagStates(): Record<FeatureFlagKey, boolean> {
  const states = {} as Record<FeatureFlagKey, boolean>;
  Object.keys(FeatureFlags).forEach(key => {
    states[key as FeatureFlagKey] = isFeatureEnabled(key as FeatureFlagKey);
  });
  return states;
}
